import { useCallback, useEffect, useRef, useState } from "react";
import { EP } from "@/lib/endpoints";
import api, { unwrap, apiError } from "@/lib/api";
import { Heart, MessageCircle, Share2, Loader2, Volume2, VolumeX, X, Send, PenSquare } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import UserAvatar from "@/components/UserAvatar";
import { useAuth } from "@/context/AuthContext";
import { resolveMediaUrl } from "@/lib/media";

/**
 * Reels — vertical snap-scroll short video experience powered by the
 * dedicated `/api:brontoy-reels/reels` group. Handles like/unlike via
 * POST + DELETE on `/reels/{id}/like` and inline comments through the
 * `/reels/{id}/comments` sub-collection.
 */
export default function Reels() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [err, setErr] = useState("");
  const [muted, setMuted] = useState(true);
  const [commentsFor, setCommentsFor] = useState(null); // reel object
  const containerRef = useRef(null);
  const seenIds = useRef(new Set());

  const fetchPage = useCallback(async (p) => {
    if (p === 1) setLoading(true);
    else setLoadingMore(true);
    try {
      const res = await api.get(EP.reelsFeed(p, 10));
      const d = unwrap(res.data);
      const list = Array.isArray(d) ? d : (d?.items || d?.reels || d?.results || []);
      const fresh = list.filter((r) => {
        const id = r.id ?? r.reel_id;
        if (id == null) return true;
        if (seenIds.current.has(id)) return false;
        seenIds.current.add(id);
        return true;
      });
      setItems((prev) => (p === 1 ? fresh : [...prev, ...fresh]));
      setHasMore(fresh.length >= 10 || d?.has_more === true);
    } catch (e) {
      setErr(apiError(e));
      setHasMore(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    seenIds.current = new Set();
    fetchPage(1);
  }, [fetchPage]);

  // Autoplay only the video whose card is closest to center of the viewport.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target.querySelector("video");
          if (!video) return;
          if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
            video.play().catch(() => { /* browsers block until muted */ });
          } else {
            video.pause();
          }
        });
        // trigger next page load when the last card is >= 50% visible
        const last = entries[entries.length - 1];
        if (last?.isIntersecting && last.target.dataset.last === "true" && hasMore && !loadingMore) {
          const nextPage = page + 1;
          setPage(nextPage);
          fetchPage(nextPage);
        }
      },
      { root: container, threshold: [0, 0.6, 0.95] }
    );
    container.querySelectorAll("[data-reel-card]").forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [items, hasMore, loadingMore, page, fetchPage]);

  if (loading) {
    return (
      <div className="h-[calc(100vh-4rem)] md:h-[calc(100vh-3.5rem)] flex items-center justify-center text-muted-foreground" data-testid="reels-loading">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }
  if (err) {
    return (
      <div className="h-[calc(100vh-4rem)] flex items-center justify-center text-destructive text-sm" data-testid="reels-error">
        {err}
      </div>
    );
  }
  if (items.length === 0) {
    return <ReelsEmpty />;
  }

  return (
    <>
      <div
        ref={containerRef}
        className="mx-auto max-w-md w-full h-[calc(100vh-4rem)] md:h-[calc(100vh-3.5rem)] snap-y snap-mandatory overflow-y-scroll no-scrollbar bg-black relative"
        data-testid="reels-container"
      >
        {/* Global mute toggle */}
        <button
          onClick={() => setMuted((m) => !m)}
          className="fixed top-20 right-4 md:top-20 md:right-6 z-40 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur flex items-center justify-center text-white"
          aria-label={muted ? "Unmute" : "Mute"}
          data-testid="reels-mute-toggle"
        >
          {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </button>

        {items.map((r, i) => (
          <ReelCard
            key={r.id ?? r.reel_id}
            reel={r}
            muted={muted}
            onOpenComments={() => setCommentsFor(r)}
            isLast={i === items.length - 1}
          />
        ))}

        {loadingMore && (
          <div className="py-10 flex justify-center text-white/70">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        )}
      </div>

      {commentsFor && (
        <CommentsSheet reel={commentsFor} onClose={() => setCommentsFor(null)} />
      )}
    </>
  );
}

function ReelsEmpty() {
  return (
    <div className="max-w-md mx-auto py-16 px-6 text-center" data-testid="reels-empty">
      <div className="w-14 h-14 rounded-full rainbow-frame mx-auto mb-5">
        <div className="rainbow-inner w-full h-full flex items-center justify-center">
          <PenSquare className="w-5 h-5" strokeWidth={1.6} />
        </div>
      </div>
      <h2 className="font-display text-2xl font-medium tracking-tight">Reels are just getting started.</h2>
      <p className="text-sm text-muted-foreground mt-2">
        Post the first Reel from the create screen — short videos land here for the whole community.
      </p>
      <Link
        to="/create?type=reel"
        className="mt-6 inline-flex items-center gap-2 h-10 px-5 rounded-full bg-foreground text-background text-sm font-medium hover:bg-foreground/90"
        data-testid="reels-empty-cta"
      >
        <PenSquare className="w-4 h-4" strokeWidth={1.8} /> Create a Reel
      </Link>
    </div>
  );
}

function ReelCard({ reel, muted, onOpenComments, isLast }) {
  const author = reel.author || reel.user || {};
  const media = reel.media || reel.media_item || {};
  const videoUrl = resolveMediaUrl(
    reel.video_url || media?.url || media?.src || reel.url || ""
  );
  const [liked, setLiked] = useState(!!(reel.viewer_liked || reel.i_liked || reel.viewer_has_liked));
  const [likeCount, setLikeCount] = useState(reel.like_count ?? reel.likes_count ?? 0);
  const commentCount = reel.comment_count ?? reel.comments_count ?? 0;

  const toggleLike = async () => {
    const next = !liked;
    setLiked(next);
    setLikeCount((c) => c + (next ? 1 : -1));
    try {
      if (next) await api.post(EP.likeReel(reel.id ?? reel.reel_id));
      else await api.delete(EP.likeReel(reel.id ?? reel.reel_id));
    } catch (e) {
      setLiked(!next);
      setLikeCount((c) => c + (next ? -1 : 1));
      toast.error(apiError(e));
    }
  };

  const share = () => {
    const url = `${window.location.origin}/reels/${reel.id ?? reel.reel_id}`;
    if (navigator.share) navigator.share({ url }).catch(() => {});
    else {
      navigator.clipboard?.writeText(url);
      toast.success("Link copied.");
    }
  };

  return (
    <div
      className="relative w-full h-full snap-start flex items-center justify-center overflow-hidden"
      data-reel-card
      data-last={isLast ? "true" : "false"}
      data-testid={`reel-card-${reel.id}`}
    >
      {videoUrl ? (
        <video
          src={videoUrl}
          className="absolute inset-0 w-full h-full object-cover"
          loop
          muted={muted}
          playsInline
          preload="metadata"
          onClick={(e) => {
            const v = e.currentTarget;
            if (v.paused) v.play().catch(() => {});
            else v.pause();
          }}
          data-testid={`reel-video-${reel.id}`}
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-white/70 text-sm">
          No video attached
        </div>
      )}

      {/* Bottom author + caption gradient */}
      <div className="absolute inset-x-0 bottom-0 p-6 pb-24 md:pb-8 bg-gradient-to-t from-black/90 via-black/50 to-transparent text-white pointer-events-none">
        <Link
          to={`/${author.username || ""}`}
          className="flex items-center gap-3 mb-3 pointer-events-auto"
          data-testid={`reel-author-${reel.id}`}
        >
          <UserAvatar user={author} size="md" />
          <div>
            <div className="text-sm font-medium">{author.display_name || author.username || "Creator"}</div>
            {author.username && <div className="text-[11px] text-white/70">@{author.username}</div>}
          </div>
        </Link>
        {(reel.caption || reel.content || reel.text) && (
          <p className="text-sm text-white/90 line-clamp-3 pointer-events-auto max-w-[85%]">
            {reel.caption || reel.content || reel.text}
          </p>
        )}
      </div>

      {/* Right side action rail */}
      <div className="absolute right-3 bottom-32 md:bottom-16 flex flex-col items-center gap-5 text-white">
        <button
          onClick={toggleLike}
          className="flex flex-col items-center gap-1"
          data-testid={`reel-like-${reel.id}`}
          aria-label={liked ? "Unlike" : "Like"}
        >
          <div className="w-12 h-12 rounded-full bg-white/10 backdrop-blur hover:bg-white/20 flex items-center justify-center transition">
            <Heart className={`w-5 h-5 ${liked ? "fill-pink-500 text-pink-500" : ""}`} strokeWidth={1.7} />
          </div>
          <span className="text-[11px] font-medium">{likeCount || ""}</span>
        </button>

        <button
          onClick={onOpenComments}
          className="flex flex-col items-center gap-1"
          data-testid={`reel-comments-${reel.id}`}
          aria-label="Comments"
        >
          <div className="w-12 h-12 rounded-full bg-white/10 backdrop-blur hover:bg-white/20 flex items-center justify-center transition">
            <MessageCircle className="w-5 h-5" strokeWidth={1.7} />
          </div>
          <span className="text-[11px] font-medium">{commentCount || ""}</span>
        </button>

        <button
          onClick={share}
          className="flex flex-col items-center gap-1"
          data-testid={`reel-share-${reel.id}`}
          aria-label="Share"
        >
          <div className="w-12 h-12 rounded-full bg-white/10 backdrop-blur hover:bg-white/20 flex items-center justify-center transition">
            <Share2 className="w-5 h-5" strokeWidth={1.7} />
          </div>
        </button>
      </div>
    </div>
  );
}

function CommentsSheet({ reel, onClose }) {
  const { user } = useAuth();
  const reelId = reel.id ?? reel.reel_id;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(EP.reelComments(reelId, 1, 30));
        const d = unwrap(res.data);
        const list = Array.isArray(d) ? d : (d?.items || d?.comments || []);
        setItems(list);
      } catch (e) {
        toast.error(apiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [reelId]);

  const submit = async () => {
    if (!text.trim()) return;
    setPosting(true);
    try {
      const res = await api.post(EP.createReelComment(reelId), { content: text.trim() });
      const d = unwrap(res.data);
      const newComment = d?.comment || d;
      setItems((prev) => [
        {
          ...newComment,
          author: {
            id: user?.id,
            username: user?.username,
            display_name: user?.display_name,
            profile_photo_url: user?.profile_photo_url,
          },
        },
        ...prev,
      ]);
      setText("");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div
        className="w-full md:max-w-md bg-background border-t md:border md:rounded-3xl border-border shadow-2xl max-h-[80vh] flex flex-col animate-in slide-in-from-bottom"
        onClick={(e) => e.stopPropagation()}
        data-testid="reel-comments-sheet"
      >
        <div className="flex items-center justify-between px-5 h-14 border-b border-border">
          <div className="text-sm font-medium">Comments</div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-foreground/5" aria-label="Close" data-testid="reel-comments-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {loading ? (
            <div className="text-center text-muted-foreground text-sm py-6">
              <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="text-center text-muted-foreground text-sm py-10">Be the first to comment.</div>
          ) : (
            items.map((c) => {
              const a = c.author || c.user || {
                id: c.user_id,
                username: c.user_id ? `user${c.user_id}` : "user",
                display_name: c.user_id ? `User ${c.user_id}` : "User",
              };
              return (
                <div key={c.id ?? c.comment_id} className="flex gap-3" data-testid={`reel-comment-${c.id}`}>
                  <UserAvatar user={a} size="sm" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px]">
                      <span className="font-medium">{a.display_name || a.username}</span>
                      <span className="ml-2 text-muted-foreground text-xs">@{a.username}</span>
                    </div>
                    <p className="text-sm mt-0.5 whitespace-pre-wrap">{c.content || c.text}</p>
                  </div>
                </div>
              );
            })
          )}
        </div>
        <div className="border-t border-border p-3 flex items-center gap-2">
          <UserAvatar user={user} size="sm" />
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submit()}
            placeholder="Add a comment…"
            className="flex-1 bg-foreground/[0.04] rounded-full h-10 px-4 text-sm focus:outline-none focus:bg-transparent focus:ring-1 focus:ring-foreground/20"
            data-testid="reel-comment-input"
          />
          <button
            onClick={submit}
            disabled={!text.trim() || posting}
            className="w-10 h-10 rounded-full bg-foreground text-background flex items-center justify-center disabled:opacity-40"
            data-testid="reel-comment-submit"
          >
            {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}
